import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Auth } from 'aws-amplify';
import config from '../config';

/*
 * Creating the global axios instance.
 */
const API = axios.create({
  baseURL: `${config.api.protocol}://${config.api.baseUri}/api/v1/`,
});

/*
 * We are setting up the request interceptor that will run before every
 * API call. This is done to ensure that the JWT authorization token is
 * valid before every request.
 *
 * AWS-Amplify Auth ensures that we always get a valid token from either
 * the browser cache or Cognito.
 */
API.interceptors.request.use(async (APIconfig: AxiosRequestConfig) => {
  const requestConfig = APIconfig;
  const token = (await Auth.currentSession()).getIdToken().getJwtToken();
  requestConfig.headers.Authorization = `Bearer ${token}`;

  return requestConfig;
});

/*
 * This response interceptor ensures that in the event that our axios
 * request fails with a status of 401, it will catch the error and update
 * the Authorization token and retry the original request once.
 */
API.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response.status === 401 && !originalRequest.retry) {
      originalRequest.retry = true;

      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      API.defaults.headers.Authorization = `Bearer ${token}`;

      return API(originalRequest);
    }

    return Promise.reject(error);
  },
);

export default API;
